import React, { ChangeEvent, useState } from 'react';
import { Add } from '@mui/icons-material';
import {
  Autocomplete,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from '@mui/material';
import { Link } from 'react-router-dom';

import HeaderToolbar from '../../../../Common/Components/HeaderToolbar';
import SearchBar from '../../../../Common/Components/Controls/SearchBar';
import CustomButton from '../../../../Common/Components/Controls/InputControls';
import { CustomerHeaderToolbarProps, EnvOption } from '../Model/CustomerProps';

export default function CustomerHeaderToolbar({
  onFilterRequest,
  onCollaborationOnlyFilter,
  customerTags,
  selectedCustomerTags,
  onSelectedCustomerTag,
  envOptions,
  selectedEnvTypes,
  onSelectedEnvType,
}: Readonly<CustomerHeaderToolbarProps>) {
  const [collaborationOnly, setCollaborationOnly] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Derive full EnvOption objects from numeric selectedEnvTypes so the
  // Autocomplete value and options share the same shape.
  const selectedEnvOptions: EnvOption[] = envOptions.filter((o) =>
    selectedEnvTypes.includes(o.value),
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    onFilterRequest(collaborationOnly, event);
  };

  const handleCollaborationToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCollaborationOnly(checked);
    onCollaborationOnlyFilter(searchText, checked);
  };

  const handleEnvChange = (
    event: React.SyntheticEvent,
    newValue: EnvOption[],
  ) => {
    onSelectedEnvType(newValue.map((o) => o.value));
  };

  const handleTagChange = (event: React.SyntheticEvent, newValue: string[]) => {
    onSelectedCustomerTag(newValue);
  };

  return (
    <HeaderToolbar
      leftContent={
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <SearchBar onChange={handleSearchChange} width="100%" />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              value={selectedCustomerTags}
              options={customerTags}
              getOptionLabel={(option) => option}
              onChange={handleTagChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Tags"
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete<EnvOption, true>
              multiple
              value={selectedEnvOptions}
              options={envOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              onChange={handleEnvChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Environments"
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
      }
      rightContent={
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="flex-start"
          flexWrap="wrap"
        >
          <Grid item xs={12} sm="auto">
            <FormControlLabel
              control={
                <Switch
                  checked={collaborationOnly}
                  onChange={handleCollaborationToggle}
                  color="primary"
                  size="small"
                />
              }
              label="Collaboration only"
            />
          </Grid>

          <Grid item xs={12} sm="auto">
            <CustomButton
              startIcon={<Add />}
              component={Link}
              to="customer/create"
              label="Add"
            />
          </Grid>
        </Grid>
      }
    />
  );
}
